<script lang="ts">
    import { page } from '$app/state';
    import type { TMarket } from '$lib/services/market/types';
    import { getRoute } from '$lib/services/navigation';
    import type { TWordpressFabricFooterTranslations } from '$lib/services/wordpress/types';
    import { footerLinkMapper } from '$lib/services/wordpress/utils';
    import { MARKET_SLUGS } from '$src/lib/services/market/slugs';
    import MediaLogos from './MediaLogos.svelte';
    import Section from './Section.svelte';

    type TProps = {
        translations: TWordpressFabricFooterTranslations;
        market: TMarket;
    };

    let { translations, market }: TProps = $props();
    const footerLogos = translations.footer_logos || [];

    const linkMapper = footerLinkMapper(market);

    const aboutLinks = translations.about_links?.map(linkMapper) || [];
    const responsibleGamingLinks = translations.responsible_gaming_links?.map(linkMapper) || [];
    const termsAndPrivacyLinks = translations.terms_and_privacy_links?.map(linkMapper) || [];
    const marketSelectHref = getRoute('/select-country') || '/';

    const flagSrc = market.slug === MARKET_SLUGS.ROW ? translations.row_flag : translations.flag;
    const marketName =
        market.slug === MARKET_SLUGS.ROW ? translations.row_market_name : translations.market_name;

    let shouldLinkToSelectCountry: boolean = $derived(!page.data.isLoggedIn);
</script>

{#snippet marketFlag()}
    <img src={flagSrc} alt={marketName} class="h-md w-md rounded-lg" />
    <div class="text-sm font-bold text-grey-50">{marketName}</div>
{/snippet}

<div class="p-sm py-md tablet:px-2xl">
    <div
        class="rounded-3xl bg-white px-md pb-md pt-sm shadow-1 tablet:pb-lg tablet:pt-md desktop:px-2xl"
    >
        <div class="flex flex-wrap gap-sm">
            <div class="flex w-1/2 flex-col gap-sm tablet:w-2/5 tablet:flex-row">
                <div class="flex flex-1 flex-col gap-sm tablet:flex-row">
                    <div class="flex-1">
                        <Section title={translations.about_title} links={aboutLinks} />
                    </div>
                    <div class="flex-1">
                        <Section
                            title={translations.responsible_gaming_title}
                            links={responsibleGamingLinks}
                        />
                    </div>
                </div>
            </div>
            <div class="flex w-1/2 flex-1 flex-col gap-sm tablet:w-3/5 tablet:flex-row">
                <div class="flex-1">
                    <Section
                        title={translations.terms_and_privacy_title}
                        links={termsAndPrivacyLinks}
                    />
                </div>
                <div class="flex-1">
                    <MediaLogos {translations} />
                </div>
            </div>
        </div>
    </div>
</div>
<div class="p-sm py-md tablet:px-2xl">
    <div class="flex flex-col divide-y divide-grey-20">
        <div class="flex justify-between pb-sm">
            {#if shouldLinkToSelectCountry}
                <a href={marketSelectHref} class="flex items-center gap-2xs">
                    {@render marketFlag()}
                </a>
            {:else}
                <div class="flex items-center gap-2xs">
                    {@render marketFlag()}
                </div>
            {/if}
            {#if translations.minimum_age_logo_link}
                <a href={translations.minimum_age_logo_link} target="_blank" rel="noreferrer">
                    <img
                        src={translations.minimum_age_logo}
                        alt={translations.minimum_age_logo}
                        class="h-md w-md"
                    />
                </a>
            {:else}
                <img
                    src={translations.minimum_age_logo}
                    alt={translations.minimum_age_logo}
                    class="h-md w-md"
                />
            {/if}
        </div>
        <div class="flex flex-col gap-sm pt-sm">
            <a href={translations.jurisdiction_link?.replace(/&amp;/g, '&')} target="_blank">
                <img
                    src={translations.jurisdiction_logo}
                    alt={translations.jurisdiction_name}
                    class="mr-auto h-xl"
                />
            </a>
            <div class="pb-sm text-sm text-grey-50">
                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                {@html translations.footer_text}
            </div>
        </div>
        <div class="flex justify-between pt-sm">
            <div class="flex gap-md">
                {#each footerLogos as logo}
                    {#if logo.enabled === 'true'}
                        <a href={logo.link} target="_blank">
                            <img src={logo.logo} alt={logo.name} class="h-md" />
                        </a>
                    {/if}
                {/each}
            </div>
            <img src={translations.iso_logo} alt={translations.iso_name} class="h-md w-md" />
        </div>
    </div>
</div>
